<template>
  <Breadcrumbs :breadcrumbs="breadcrumbs" />
  <BlogDetail v-if="data?.websitePage" :blog-post="data.websitePage" />
</template>

<script setup lang="ts">
import { useRouteParams } from "@vueuse/router";
import { useWebsitePageQuery } from "~/graphql/generated";

definePageMeta({
  disableHrefLangs: true,
});

const path = useRouteParams<string>("slug");

/*
 * Fetch the blog post and handle error if not found
 */
const { data, error, executeQuery } = await useWebsitePageQuery({
  variables: {
    websitePage: {
      path: `/${path.value}`,
    },
  },
});

if (error?.value || !data.value?.websitePage) {
  throw createError({ statusCode: 404, fatal: true });
}

/*
 * Breadcrumbs
 */
const { t, locale } = useI18n();
// Set the breadcrumbs
const breadcrumbs = ref([
  {
    to: "/blog",
    text: t("blog.title"),
  },
  {
    to: data.value?.websitePage?.path ?? "/",
    text: data.value?.websitePage?.title ?? "",
  },
]);

watch(
  locale,
  () => {
    // Refetch the post when the language changes to avoid stale localized content.
    executeQuery({ requestPolicy: "network-only" });
  },
  { flush: "post" },
);

const { currentHrefLangs } = useContentHrefLangs(
  computed(() => data.value?.websitePage),
  "/blog",
);

/*
 * Set the page title and meta description
 */
const pageTitle = computed(() => data.value?.websitePage?.metaTitle || data.value?.websitePage?.title || undefined);
const pageDescription = computed(() => data.value?.websitePage?.metaDescription || undefined);
const pageImage = computed(() => {
  const page = data.value?.websitePage;
  return page && "coverImage" in page ? page.coverImage : undefined;
});

useHead(() => ({
  title: pageTitle.value,
  meta: [
    {
      name: "description",
      content: pageDescription.value,
    },
  ],
  link: currentHrefLangs.value.map((link) => ({
    id: link.hid,
    rel: link.rel,
    href: link.href,
    hreflang: link.hreflang,
  })),
}));

useSeoMeta({
  ogTitle: pageTitle,
  ogDescription: pageDescription,
  ogImage: pageImage,
  ogType: "article",
});
</script>
